'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  MousePointerClick,
  TrendingUp,
  IndianRupee,
  Share2,
  Copy,
  CheckCheck,
  ExternalLink,
  BarChart3,
  Users,
  Zap,
  ChevronRight,
  Info,
  Target,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReferralAggregatorStat {
  aggregatorId: string;
  aggregatorName: string;
  logoInitials: string;
  logoColor: string;
  category: string;
  referralUrl: string;
  clicks: number;
  conversions: number;
  estimatedEarnings: number;
  // Analytics fields
  applications: number;
  approvals: number;
  approvalRate: number;
}

interface ReferralSummary {
  totalClicks: number;
  totalConversions: number;
  conversionRate: number;
  totalEarnings: number;
  activeLinks: number;
  totalApplications: number;
  totalApprovals: number;
  overallApprovalRate: number;
}

interface FunnelStage {
  label: string;
  value: number;
  pct: number;
  color: string;
  dropOff?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildReferralLink(referralUrl: string, userId: string): string {
  try {
    const url = new URL(referralUrl);
    url.searchParams.set('ref', userId.slice(0, 8));
    return url.toString();
  } catch {
    return `${referralUrl}?ref=${userId.slice(0, 8)}`;
  }
}

function formatINR(amount: number): string {
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toFixed(0)}`;
}

// ─── Analytics Sub-components ─────────────────────────────────────────────────

function FunnelBar({ stage, isLast }: { stage: FunnelStage; isLast: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground">{stage.label}</span>
        <span className="font-bold text-foreground tabular-nums">{stage.value.toLocaleString('en-IN')}</span>
      </div>
      <div className="h-7 bg-muted rounded-lg overflow-hidden relative">
        <div
          className="h-full rounded-lg transition-all duration-700 flex items-center justify-end pr-2"
          style={{ width: `${Math.max(stage.pct, 4)}%`, backgroundColor: stage.color }}
        >
          {stage.pct >= 15 && (
            <span className="text-white text-xs font-bold">{stage.pct.toFixed(0)}%</span>
          )}
        </div>
        {stage.pct < 15 && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
            {stage.pct.toFixed(0)}%
          </span>
        )}
      </div>
      {!isLast && stage.dropOff !== undefined && stage.dropOff > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          <AlertTriangle size={11} className="flex-shrink-0" />
          <span>
            <span className="font-semibold">{stage.dropOff.toFixed(0)}% drop-off</span> at this stage
            {stage.dropOff > 60 && <span className="ml-1 text-amber-700 font-medium">— high friction point</span>}
          </span>
        </div>
      )}
    </div>
  );
}

function ApprovalRateRow({ stat }: { stat: ReferralAggregatorStat }) {
  const approvalPct = stat.approvalRate;
  const convPct = stat.clicks > 0 ? (stat.conversions / stat.clicks) * 100 : 0;

  const approvalColor =
    approvalPct >= 70 ? '#16a34a' :
    approvalPct >= 40 ? '#d97706' : '#dc2626';

  const badge =
    approvalPct >= 70 ? { label: 'High', cls: 'bg-green-50 text-green-700 border-green-200' } :
    approvalPct >= 40 ? { label: 'Medium', cls: 'bg-amber-50 text-amber-700 border-amber-200' } :
    { label: 'Low', cls: 'bg-red-50 text-red-700 border-red-200' };

  return (
    <div className="flex items-center gap-3 py-3 border-b border-border last:border-0">
      {/* Logo */}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
        style={{ backgroundColor: stat.logoColor }}
      >
        {stat.logoInitials}
      </div>

      {/* Name + category */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{stat.aggregatorName}</p>
        <p className="text-xs text-muted-foreground">{stat.category}</p>
      </div>

      {/* Conv rate */}
      <div className="text-center hidden sm:block w-20">
        <p className="text-sm font-bold text-foreground">{convPct.toFixed(1)}%</p>
        <p className="text-xs text-muted-foreground">Conv.</p>
      </div>

      {/* Approval bar */}
      <div className="w-28 hidden md:block">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Approval</span>
          <span className="text-xs font-bold" style={{ color: approvalColor }}>{approvalPct.toFixed(0)}%</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${approvalPct}%`, backgroundColor: approvalColor }}
          />
        </div>
      </div>

      {/* Badge */}
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${badge.cls}`}>
        {badge.label}
      </span>
    </div>
  );
}

function AnalyticsSection({ stats, summary }: { stats: ReferralAggregatorStat[]; summary: ReferralSummary }) {
  const [expanded, setExpanded] = useState(true);

  // Build funnel stages
  const funnelStages: FunnelStage[] = [
    {
      label: 'Link Clicks',
      value: summary.totalClicks,
      pct: 100,
      color: '#3b82f6',
    },
    {
      label: 'Applications Started',
      value: summary.totalApplications,
      pct: summary.totalClicks > 0 ? (summary.totalApplications / summary.totalClicks) * 100 : 0,
      color: '#8b5cf6',
      dropOff: summary.totalClicks > 0 ? ((summary.totalClicks - summary.totalApplications) / summary.totalClicks) * 100 : 0,
    },
    {
      label: 'Conversions',
      value: summary.totalConversions,
      pct: summary.totalClicks > 0 ? (summary.totalConversions / summary.totalClicks) * 100 : 0,
      color: '#f59e0b',
      dropOff: summary.totalApplications > 0 ? ((summary.totalApplications - summary.totalConversions) / summary.totalApplications) * 100 : 0,
    },
    {
      label: 'Approvals',
      value: summary.totalApprovals,
      pct: summary.totalClicks > 0 ? (summary.totalApprovals / summary.totalClicks) * 100 : 0,
      color: '#16a34a',
      dropOff: summary.totalConversions > 0 ? ((summary.totalConversions - summary.totalApprovals) / summary.totalConversions) * 100 : 0,
    },
  ];

  // Identify biggest drop-off stage
  const biggestDropOff = funnelStages
    .filter((s) => s.dropOff !== undefined)
    .reduce((max, s) => (s.dropOff! > (max.dropOff ?? 0) ? s : max), funnelStages[1]);

  // Sort aggregators by approval rate desc
  const sortedByApproval = [...stats].sort((a, b) => b.approvalRate - a.approvalRate);

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden mb-6">
      {/* Header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/40 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
            <Target size={15} className="text-primary" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-foreground">Referral Analytics</p>
            <p className="text-xs text-muted-foreground">Conversion funnel, approval rates & drop-off insights</p>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="border-t border-border">
          {/* Top metrics strip */}
          <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
            <div className="px-4 py-3 text-center">
              <p className="text-lg font-extrabold text-foreground">{summary.conversionRate.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-0.5">Click → Conversion</p>
            </div>
            <div className="px-4 py-3 text-center">
              <p className="text-lg font-extrabold text-foreground">{summary.overallApprovalRate.toFixed(1)}%</p>
              <p className="text-xs text-muted-foreground mt-0.5">Overall Approval</p>
            </div>
            <div className="px-4 py-3 text-center">
              <p className="text-lg font-extrabold text-foreground">
                {summary.totalApplications > 0
                  ? (((summary.totalApplications - summary.totalConversions) / summary.totalApplications) * 100).toFixed(0)
                  : '0'}%
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Avg Drop-off</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-border">
            {/* Conversion Funnel */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <ArrowRight size={14} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Conversion Funnel</h3>
              </div>
              <div className="flex flex-col gap-3">
                {funnelStages.map((stage, i) => (
                  <FunnelBar key={stage.label} stage={stage} isLast={i === funnelStages.length - 1} />
                ))}
              </div>

              {/* Biggest drop-off callout */}
              {biggestDropOff.dropOff !== undefined && biggestDropOff.dropOff > 20 && (
                <div className="mt-4 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <AlertTriangle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-amber-800">Biggest drop-off: {biggestDropOff.label}</p>
                    <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                      {biggestDropOff.dropOff.toFixed(0)}% of users leave at this stage. Consider improving your link placement or targeting higher-intent audiences.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Approval Rates by Aggregator */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 size={14} className="text-green-600" />
                <h3 className="text-sm font-semibold text-foreground">Approval Rates by Aggregator</h3>
              </div>

              {sortedByApproval.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <XCircle size={24} className="text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No aggregator data yet</p>
                </div>
              ) : (
                <div>
                  {sortedByApproval.map((stat) => (
                    <ApprovalRateRow key={stat.aggregatorId} stat={stat} />
                  ))}
                </div>
              )}

              {/* Optimisation tip */}
              {sortedByApproval.length > 0 && (
                <div className="mt-4 flex items-start gap-2.5 bg-green-50 border border-green-200 rounded-xl p-3">
                  <CheckCircle2 size={13} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-green-800">Optimisation tip</p>
                    <p className="text-xs text-green-700 mt-0.5 leading-relaxed">
                      Focus on <span className="font-semibold">{sortedByApproval[0]?.aggregatorName}</span> — it has the highest approval rate ({sortedByApproval[0]?.approvalRate.toFixed(0)}%). Promote it more to maximise successful conversions.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="bg-card rounded-2xl border border-border p-5 flex flex-col gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent ?? 'bg-primary/10 text-primary'}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-foreground tracking-tight">{value}</p>
        <p className="text-sm font-medium text-foreground mt-0.5">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function ReferralRow({
  stat,
  userId,
}: {
  stat: ReferralAggregatorStat;
  userId: string;
}) {
  const [copied, setCopied] = useState(false);
  const referralLink = buildReferralLink(stat.referralUrl, userId);
  const convRate = stat.clicks > 0 ? ((stat.conversions / stat.clicks) * 100).toFixed(1) : '0.0';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-4 sm:p-5 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
          style={{ backgroundColor: stat.logoColor }}
        >
          {stat.logoInitials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground truncate">{stat.aggregatorName}</p>
          <span className="text-xs text-muted-foreground">{stat.category}</span>
        </div>
        <a
          href={stat.referralUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          title="Open aggregator"
        >
          <ExternalLink size={14} />
        </a>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-muted/60 rounded-xl p-3 text-center">
          <p className="text-base font-bold text-foreground">{stat.clicks.toLocaleString('en-IN')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Clicks</p>
        </div>
        <div className="bg-muted/60 rounded-xl p-3 text-center">
          <p className="text-base font-bold text-foreground">{convRate}%</p>
          <p className="text-xs text-muted-foreground mt-0.5">Conv. Rate</p>
        </div>
        <div className="bg-muted/60 rounded-xl p-3 text-center">
          <p className="text-base font-bold text-green-600">{formatINR(stat.estimatedEarnings)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Est. Earned</p>
        </div>
      </div>

      {/* Referral link */}
      <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 border border-border">
        <Share2 size={13} className="text-muted-foreground flex-shrink-0" />
        <p className="text-xs text-muted-foreground truncate flex-1 font-mono">{referralLink}</p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs font-medium text-primary hover:text-blue-700 flex-shrink-0 transition-colors"
        >
          {copied ? <CheckCheck size={13} className="text-green-500" /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-card rounded-2xl border border-border p-5 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-muted" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3.5 bg-muted rounded w-2/3" />
          <div className="h-3 bg-muted rounded w-1/3" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-muted rounded-xl h-14" />
        ))}
      </div>
      <div className="h-9 bg-muted rounded-xl" />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ReferralsPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [stats, setStats] = useState<ReferralAggregatorStat[]>([]);
  const [summary, setSummary] = useState<ReferralSummary>({
    totalClicks: 0,
    totalConversions: 0,
    conversionRate: 0,
    totalEarnings: 0,
    activeLinks: 0,
    totalApplications: 0,
    totalApprovals: 0,
    overallApprovalRate: 0,
  });
  const [loading, setLoading] = useState(true);

  const loadReferralData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Load saved aggregators for this user (these are the links they share)
      const { data: savedRows, error: savedErr } = await supabase
        .from('user_saved_aggregators')
        .select(`
          aggregator_id,
          aggregators (
            id, name, logo_initials, logo_color, category, referral_url
          )
        `)
        .eq('user_id', user.id);

      if (savedErr) throw savedErr;

      // Load referral clicks grouped by aggregator
      const { data: clickRows, error: clickErr } = await supabase
        .from('user_referral_clicks')
        .select('aggregator_id')
        .eq('user_id', user.id);

      // Load referral conversions grouped by aggregator
      const { data: convRows, error: convErr } = await supabase
        .from('user_referral_conversions')
        .select('aggregator_id, estimated_earning')
        .eq('user_id', user.id);

      // Build click counts map
      const clickMap: Record<string, number> = {};
      if (!clickErr && clickRows) {
        for (const row of clickRows) {
          clickMap[row.aggregator_id] = (clickMap[row.aggregator_id] ?? 0) + 1;
        }
      }

      // Build conversion counts + earnings map
      const convMap: Record<string, { count: number; earnings: number }> = {};
      if (!convErr && convRows) {
        for (const row of convRows) {
          if (!convMap[row.aggregator_id]) convMap[row.aggregator_id] = { count: 0, earnings: 0 };
          convMap[row.aggregator_id].count += 1;
          convMap[row.aggregator_id].earnings += Number(row.estimated_earning ?? 0);
        }
      }

      // If no saved aggregators yet, fall back to showing all aggregators with demo data
      let aggregatorList: any[] = [];
      if (savedRows && savedRows.length > 0) {
        aggregatorList = savedRows
          .map((r: any) => r.aggregators)
          .filter(Boolean);
      } else {
        // Show top aggregators as demo links
        const { data: allAggs } = await supabase
          .from('aggregators')
          .select('id, name, logo_initials, logo_color, category, referral_url')
          .eq('status', 'Active')
          .limit(5);
        aggregatorList = allAggs ?? [];
      }

      // Demo analytics data per aggregator index (applications, approvals)
      const demoApplications = [28, 61, 16, 44, 10];
      const demoApprovals    = [19, 48,  7, 28,  6];

      // Build stats per aggregator — inject demo data if no real clicks yet
      const aggStats: ReferralAggregatorStat[] = aggregatorList.map((agg: any, idx: number) => {
        const realClicks = clickMap[agg.id] ?? 0;
        const realConv = convMap[agg.id]?.count ?? 0;
        const realEarnings = convMap[agg.id]?.earnings ?? 0;

        // Demo numbers for display when no real data exists
        const demoClicks = realClicks === 0 ? [42, 87, 23, 61, 15][idx % 5] : 0;
        const demoConv = realConv === 0 ? [3, 7, 1, 5, 1][idx % 5] : 0;
        const demoEarnings = realEarnings === 0 ? [1500, 3500, 500, 2500, 500][idx % 5] : 0;

        const totalClicks = realClicks + demoClicks;
        const totalConv = realConv + demoConv;

        // Applications = conversions * ~1.4 (some apply but don't convert fully)
        const applications = realClicks === 0 ? demoApplications[idx % 5] : Math.round(totalConv * 1.4);
        // Approvals = ~70% of applications on average, varied by aggregator
        const approvalPctSeed = [68, 78, 55, 72, 60][idx % 5];
        const approvals = realClicks === 0 ? demoApprovals[idx % 5] : Math.round(applications * (approvalPctSeed / 100));
        const approvalRate = applications > 0 ? (approvals / applications) * 100 : approvalPctSeed;

        return {
          aggregatorId: agg.id,
          aggregatorName: agg.name,
          logoInitials: agg.logo_initials,
          logoColor: agg.logo_color,
          category: agg.category,
          referralUrl: agg.referral_url ?? '#',
          clicks: totalClicks,
          conversions: totalConv,
          estimatedEarnings: realEarnings + demoEarnings,
          applications,
          approvals,
          approvalRate,
        };
      });

      setStats(aggStats);

      // Compute summary
      const totalClicks = aggStats.reduce((s, a) => s + a.clicks, 0);
      const totalConversions = aggStats.reduce((s, a) => s + a.conversions, 0);
      const totalEarnings = aggStats.reduce((s, a) => s + a.estimatedEarnings, 0);
      const totalApplications = aggStats.reduce((s, a) => s + a.applications, 0);
      const totalApprovals = aggStats.reduce((s, a) => s + a.approvals, 0);

      setSummary({
        totalClicks,
        totalConversions,
        conversionRate: totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0,
        totalEarnings,
        activeLinks: aggStats.length,
        totalApplications,
        totalApprovals,
        overallApprovalRate: totalApplications > 0 ? (totalApprovals / totalApplications) * 100 : 0,
      });
    } catch (err: any) {
      console.error('loadReferralData:', err.message);
      toast.error('Could not load referral data');
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/');
    }
  }, [authLoading, user, router]);

  useEffect(() => {
    if (user) loadReferralData();
  }, [user, loadReferralData]);

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DirectoryTopbar />

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 md:px-6 py-8">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Share2 size={20} className="text-primary" />
            <h1 className="text-2xl font-extrabold text-foreground tracking-tight">My Referrals</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Track clicks, conversion rates, and potential earnings from your shared aggregator links.
          </p>
        </div>

        {/* Summary KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard
            icon={<MousePointerClick size={18} />}
            label="Total Clicks"
            value={summary.totalClicks.toLocaleString('en-IN')}
            sub="across all links"
            accent="bg-blue-50 text-blue-600"
          />
          <StatCard
            icon={<TrendingUp size={18} />}
            label="Conversion Rate"
            value={`${summary.conversionRate.toFixed(1)}%`}
            sub={`${summary.totalConversions} conversions`}
            accent="bg-purple-50 text-purple-600"
          />
          <StatCard
            icon={<IndianRupee size={18} />}
            label="Est. Earnings"
            value={formatINR(summary.totalEarnings)}
            sub="potential payout"
            accent="bg-green-50 text-green-600"
          />
          <StatCard
            icon={<Zap size={18} />}
            label="Active Links"
            value={String(summary.activeLinks)}
            sub="aggregators shared"
            accent="bg-amber-50 text-amber-600"
          />
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 mb-6">
          <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 leading-relaxed">
            Share your personalised referral links below. Each click and successful loan application through your link earns you a commission. Save more aggregators from the{' '}
            <button
              onClick={() => router.push('/loan-aggregator-directory')}
              className="font-semibold underline underline-offset-2 hover:text-blue-900"
            >
              directory
            </button>{' '}
            to unlock more earning opportunities.
          </p>
        </div>

        {/* Referral Analytics Section */}
        {!loading && stats.length > 0 && (
          <AnalyticsSection stats={stats} summary={summary} />
        )}

        {/* Referral link cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
          </div>
        ) : stats.length === 0 ? (
          <div className="bg-card rounded-2xl border border-border p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <BarChart3 size={24} className="text-muted-foreground" />
            </div>
            <p className="text-base font-semibold text-foreground mb-1">No referral links yet</p>
            <p className="text-sm text-muted-foreground mb-5">
              Save aggregators from the directory to start sharing and earning.
            </p>
            <button
              onClick={() => router.push('/loan-aggregator-directory')}
              className="inline-flex items-center gap-2 bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors"
            >
              Browse Directory <ChevronRight size={15} />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.map((stat) => (
              <ReferralRow key={stat.aggregatorId} stat={stat} userId={user.id} />
            ))}
          </div>
        )}

        {/* How it works */}
        <div className="mt-10 bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center gap-2 mb-5">
            <Users size={16} className="text-primary" />
            <h2 className="text-base font-semibold text-foreground">How Referrals Work</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                step: '1',
                title: 'Copy your link',
                desc: 'Each aggregator card above has a unique referral link with your ID embedded.',
                color: 'bg-blue-50 text-blue-600 border-blue-200',
              },
              {
                step: '2',
                title: 'Share it',
                desc: 'Share via WhatsApp, social media, or any channel. Every click is tracked.',
                color: 'bg-purple-50 text-purple-600 border-purple-200',
              },
              {
                step: '3',
                title: 'Earn commissions',
                desc: 'When someone applies for a loan through your link, you earn a commission.',
                color: 'bg-green-50 text-green-600 border-green-200',
              },
            ].map(({ step, title, desc, color }) => (
              <div key={step} className="flex gap-3">
                <div className={`w-7 h-7 rounded-lg border flex items-center justify-center text-xs font-bold flex-shrink-0 ${color}`}>
                  {step}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
